import { klaviyoLists } from "@/config";

const KLAVIYO_CLIENT_API = "https://a.klaviyo.com/client";
const KLAVIYO_REVISION = "2024-05-15";

/**
 * Subscribe a profile to Klaviyo back-in-stock notifications for a variant.
 *
 * @param {Object} params
 * @param {string} params.currencyCode - Lowercase currency code, e.g. "gbp"
 * @param {string} params.variantId   - Shopify variant GID or numeric ID
 * @param {string} params.email
 * @param {string} [params.phone]     - E.164 phone number; adds the SMS channel
 * @returns {Promise<boolean>} true when Klaviyo accepted the subscription
 */
export const subscribeBackInStock = async ({
  currencyCode,
  variantId,
  email,
  phone,
}) => {
  const klaviyo = klaviyoLists[currencyCode];
  const numericVariantId = variantId.includes("ProductVariant/")
    ? variantId.split("ProductVariant/")[1]
    : variantId;

  const payload = {
    data: {
      type: "back-in-stock-subscription",
      attributes: {
        profile: {
          data: {
            type: "profile",
            // Klaviyo's BIS profile only accepts email/phone_number;
            // first_name is rejected with a 400 "Invalid input".
            attributes: {
              email,
              ...(phone && { phone_number: phone }),
            },
          },
        },
        channels: ["EMAIL", ...(phone ? ["SMS"] : [])],
      },
      relationships: {
        variant: {
          data: {
            type: "catalog-variant",
            id: `$shopify:::$default:::${numericVariantId}`,
          },
        },
      },
    },
  };

  const response = await fetch(
    `${KLAVIYO_CLIENT_API}/back-in-stock-subscriptions?company_id=${klaviyo.accountId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    }
  );

  return response.status === 202;
};

/**
 * Subscribe a profile to the region's Klaviyo marketing list.
 *
 * @param {Object} params
 * @param {string} params.currencyCode - Lowercase currency code, e.g. "gbp"
 * @param {string} params.email
 * @param {string} [params.phone]        - E.164 phone number
 * @param {string} [params.firstName]
 * @param {string} [params.customSource] - Klaviyo custom_source label
 * @returns {Promise<boolean>} true when Klaviyo accepted the subscription
 */
export const subscribeToMarketingList = async ({
  currencyCode,
  email,
  phone,
  firstName,
  customSource = "bis_signup",
}) => {
  const klaviyo = klaviyoLists[currencyCode];

  const payload = {
    data: {
      type: "subscription",
      attributes: {
        custom_source: customSource,
        profile: {
          data: {
            type: "profile",
            attributes: {
              email,
              ...(phone && { phone_number: phone }),
              ...(firstName && { first_name: firstName }),
            },
          },
        },
      },
      relationships: {
        list: {
          data: {
            type: "list",
            id: klaviyo.listId,
          },
        },
      },
    },
  };

  const response = await fetch(
    `${KLAVIYO_CLIENT_API}/subscriptions/?company_id=${klaviyo.accountId}`,
    {
      method: "POST",
      headers: {
        "content-type": "application/json",
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    }
  );

  return response.status === 202;
};

/**
 * Add a profile to the SMS list via the Rat & Boa Node app.
 *
 * @param {Object} params
 * @param {string} params.email
 * @param {string} params.phone - E.164 phone number
 * @returns {Promise<boolean>} true when the Node app accepted the request
 */
export const subscribeSmsList = async ({ email, phone }) => {
  const response = await fetch(
    `${process.env.RATANDBOA_NODE_APP_URL}/klaviyo/sms-list`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        phone_number: phone,
      }),
    }
  );

  return response.ok;
};
